import { CheckCircle, Clock, UploadSimple, PlayCircle, Circle } from '@phosphor-icons/react';

/**
 * Status marks for a lesson row, on the Discover card's icon-plus-text pattern
 * (CL-1) rather than a coloured dot.
 *
 * A dot carries status in hue alone, which fails for anyone who cannot separate
 * the hues and reads as decoration to everyone else. The shape does the work
 * here; the colour reinforces it.
 *
 * Values are CL-1's, so no new hues enter the palette. `weight="bold"` for the
 * same reason CL-1 gives: these are iOS system colours tuned for SF Symbols,
 * and a thin Phosphor stroke renders them lighter than intended.
 */
const STATUS_ICON: Record<string, { Icon: typeof CheckCircle; color: string }> = {
  // Player side
  'Ready to submit': { Icon: UploadSimple, color: '#FF9500' },
  'Awaiting analysis': { Icon: Clock, color: '#54C3EA' },
  'Ready to watch': { Icon: PlayCircle, color: '#1A6B8A' },
  Complete: { Icon: CheckCircle, color: '#34C759' },
  // Coach side
  'Awaiting upload': { Icon: Clock, color: '#FF9500' },
  // The coach owes the video. Orange for the same reason 'Ready to submit' is
  // orange on the player side: it is the viewer's turn to act.
  'Awaiting your review': { Icon: Clock, color: '#FF9500' },
  // Transcoding, not a queue the coach can act on. Oasis rather than orange,
  // the same distinction the player side draws with 'Awaiting analysis':
  // orange is your turn, blue is in flight. Key carries the ellipsis because
  // the visible copy does.
  /* The coach's own submission going up. Same clock and same oasis as the
     player's upload below it: both are in flight and neither is the coach's
     turn to act. */
  'Sending...': { Icon: Clock, color: '#54C3EA' },
  /* Kept, and NOT renamed to match the row above. This key is shared: it also
     serves COACH_LABEL's `uploading` and `processing` states in
     lessonStateCopy, which describe the PLAYER'S video being processed on the
     coach's training program screen. That is a different event from the coach
     sending a lesson, it is still correctly called processing, and renaming
     this key would have silently dropped its icon to the fallback ring. */
  'Processing video...': { Icon: Clock, color: '#54C3EA' },
  Delivered: { Icon: CheckCircle, color: '#34C759' },
};

/** Anything unmapped: a neutral ring, never a colour that implies a state. */
const FALLBACK = { Icon: Circle, color: 'rgba(60,60,67,0.6)' };

export default function LessonStatusIcon({
  label,
  size = 14,
  selected = false,
}: {
  label: string;
  size?: number;
  /** On a filled biscayne row the hues lose their contrast, so the mark goes white. */
  selected?: boolean;
}) {
  const { Icon, color } = STATUS_ICON[label] ?? FALLBACK;
  return (
    <Icon
      size={size}
      weight="bold"
      color={selected ? 'rgba(255,255,255,0.8)' : color}
      className="flex-shrink-0"
      aria-hidden="true"
    />
  );
}
